import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  List,
  Transfer,
  Typography,
  Card,
  Checkbox,
  Spin,
} from "antd";
import { useNavigate } from "react-router-dom";
import { UserOutlined, TeamOutlined, EnvironmentOutlined, BankOutlined } from "@ant-design/icons";
import api from "../../../../axiosConfig";

const { Title, Text } = Typography;

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuariosModalVisible, setUsuariosModalVisible] = useState(false);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [usuariosAsignados, setUsuariosAsignados] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);

  // Estados para gestión de zonas
  const [zonasModalVisible, setZonasModalVisible] = useState(false);
  const [zonasDisponibles, setZonasDisponibles] = useState([]);
  const [zonasAsignadas, setZonasAsignadas] = useState([]);
  const [zonasLoading, setZonasLoading] = useState(false);
  const [guardandoZonas, setGuardandoZonas] = useState(false);

  // Estados para gestión de clientes/EPS
  const [epsModalVisible, setEpsModalVisible] = useState(false);
  const [epsDisponibles, setEpsDisponibles] = useState([]);
  const [epsAsignadas, setEpsAsignadas] = useState([]);
  const [epsLoading, setEpsLoading] = useState(false);
  const [guardandoEps, setGuardandoEps] = useState(false);

  const navigate = useNavigate();

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (error) {
      console.error("Error obteniendo roles:", error);
      message.error("Error cargando roles");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsuariosRol = async (rolId) => {
    setTransferLoading(true);
    try {
      const { data } = await api.get(`/roles/${rolId}/usuarios`);
      setUsuariosAsignados(data.asignados);
      setUsuariosDisponibles(data.disponibles);
    } catch (error) {
      console.error("Error obteniendo usuarios del rol:", error);
      message.error("Error cargando usuarios");
    } finally {
      setTransferLoading(false);
    }
  };

  const handleGestionarUsuarios = async (rol) => {
    setRolSeleccionado(rol);
    setUsuariosModalVisible(true);
    await fetchUsuariosRol(rol.id);
  };

  const handleTransferChange = async (targetKeys) => {
    setTransferLoading(true);
    try {
      // Obtener usuarios a asignar (los que están en targetKeys pero no en usuariosAsignados)
      const usuariosAAgregar = targetKeys.filter(
        (key) => !usuariosAsignados.some((u) => u.id === key)
      );

      // Obtener usuarios a quitar (los que están en usuariosAsignados pero no en targetKeys)
      const usuariosAQuitar = usuariosAsignados
        .filter((u) => !targetKeys.includes(u.id))
        .map((u) => u.id);

      // Asignar nuevos usuarios
      for (const usuarioId of usuariosAAgregar) {
        await api.post(`/roles/${rolSeleccionado.id}/usuarios`, {
          usuario_id: usuarioId,
        });
      }

      // Quitar usuarios
      for (const usuarioId of usuariosAQuitar) {
        await api.delete(`/roles/${rolSeleccionado.id}/usuarios/${usuarioId}`);
      }

      message.success("Usuarios actualizados correctamente");
      await fetchUsuariosRol(rolSeleccionado.id);
    } catch (error) {
      console.error("Error actualizando usuarios:", error);
      message.error("Error actualizando usuarios");
    } finally {
      setTransferLoading(false);
    }
  };

  // Funciones para gestión de zonas
  const fetchZonasRol = async (rolId) => {
    setZonasLoading(true);
    try {
      const { data } = await api.get(`/roles/${rolId}/zonas`);
      setZonasDisponibles(data.zonas_disponibles || []);
      setZonasAsignadas(data.zonas_asignadas || []);
    } catch (error) {
      console.error("Error obteniendo zonas del rol:", error);
      message.error("Error cargando zonas");
    } finally {
      setZonasLoading(false);
    }
  };

  const handleGestionarZonas = async (rol) => {
    setRolSeleccionado(rol);
    setZonasModalVisible(true);
    await fetchZonasRol(rol.id);
  };

  const handleZonaChange = (zonaId, checked) => {
    if (checked) {
      setZonasAsignadas([...zonasAsignadas, zonaId]);
    } else {
      setZonasAsignadas(zonasAsignadas.filter((id) => id !== zonaId));
    }
  };

  const handleGuardarZonas = async () => {
    setGuardandoZonas(true);
    try {
      await api.post(`/roles/${rolSeleccionado.id}/zonas`, {
        zonas: zonasAsignadas,
      });
      message.success("Zonas actualizadas correctamente");
      setZonasModalVisible(false);
    } catch (error) {
      console.error("Error guardando zonas:", error);
      message.error("Error guardando zonas");
    } finally {
      setGuardandoZonas(false);
    }
  };

  const handleSeleccionarTodasZonas = (checked) => {
    if (checked) {
      setZonasAsignadas(zonasDisponibles.map((z) => z.id));
    } else {
      setZonasAsignadas([]);
    }
  };

  // Funciones para gestión de clientes/EPS
  const fetchEpsRol = async (rolId) => {
    setEpsLoading(true);
    try {
      const { data } = await api.get(`/roles/${rolId}/clientes`);
      setEpsDisponibles(data.clientes_disponibles || []);
      setEpsAsignadas(data.clientes_asignados || []);
    } catch (error) {
      console.error("Error obteniendo EPS del rol:", error);
      message.error("Error cargando EPS");
    } finally {
      setEpsLoading(false);
    }
  };

  const handleGestionarEps = async (rol) => {
    setRolSeleccionado(rol);
    setEpsModalVisible(true);
    await fetchEpsRol(rol.id);
  };

  const handleEpsChange = (epsId, checked) => {
    if (checked) {
      setEpsAsignadas([...epsAsignadas, epsId]);
    } else {
      setEpsAsignadas(epsAsignadas.filter((id) => id !== epsId));
    }
  };

  const handleGuardarEps = async () => {
    setGuardandoEps(true);
    try {
      await api.post(`/roles/${rolSeleccionado.id}/clientes`, {
        clientes: epsAsignadas,
      });
      message.success("EPS actualizadas correctamente");
      setEpsModalVisible(false);
    } catch (error) {
      console.error("Error guardando EPS:", error);
      message.error("Error guardando EPS");
    } finally {
      setGuardandoEps(false);
    }
  };

  const handleSeleccionarTodasEps = (checked) => {
    if (checked) {
      setEpsAsignadas(epsDisponibles.map((e) => e.id));
    } else {
      setEpsAsignadas([]);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const columns = [
    {
      title: "Nombre del Rol",
      dataIndex: "nombre_rol",
      key: "nombre_rol",
      render: (text) => (
        <Text strong style={{ fontSize: "16px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<UserOutlined />}
            onClick={() => navigate(`/modules/roles/editar/${record.id}`)}
          >
            Editar
          </Button>
          <Button
            type="default"
            icon={<TeamOutlined />}
            onClick={() => handleGestionarUsuarios(record)}
          >
            Gestionar Usuarios
          </Button>
          <Button
            type="default"
            icon={<EnvironmentOutlined />}
            onClick={() => handleGestionarZonas(record)}
            style={{ borderColor: "#52c41a", color: "#52c41a" }}
          >
            Gestionar Zonas
          </Button>
          <Button
            type="default"
            icon={<BankOutlined />}
            onClick={() => handleGestionarEps(record)}
            style={{ borderColor: "#1890ff", color: "#1890ff" }}
          >
            Gestionar EPS
          </Button>
          <Button
            danger
            onClick={async () => {
              try {
                await api.delete(`/roles/${record.id}`);
                message.success("Rol eliminado correctamente");
                fetchRoles();
              } catch {
                message.error("Error eliminando rol");
              }
            }}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Title level={4} style={{ margin: 0 }}>
              Gestión de Roles
            </Title>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/modules/roles/nuevo")}
            >
              Crear nuevo Rol
            </Button>
          </div>
        }
        variant="borderless"
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} roles`,
          }}
        />
      </Card>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <TeamOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
            <span style={{ fontSize: "18px" }}>
              Gestionar Usuarios - {rolSeleccionado?.nombre_rol}
            </span>
          </div>
        }
        open={usuariosModalVisible}
        onCancel={() => setUsuariosModalVisible(false)}
        footer={null}
        width={1200}
        styles={{
          body: {
            padding: "32px",
          },
        }}
      >
        <Card variant="borderless">
          <Transfer
            dataSource={[...usuariosDisponibles, ...usuariosAsignados].map(
              (usuario) => ({
                key: usuario.id,
                title: usuario.nombre,
                description: usuario.correo_electronico,
              })
            )}
            titles={[
              <div style={{ textAlign: "center" }}>
                <Title level={4}>Usuarios Disponibles</Title>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Seleccione los usuarios a asignar
                </Text>
              </div>,
              <div style={{ textAlign: "center" }}>
                <Title level={4}>Usuarios Asignados</Title>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Usuarios con este rol
                </Text>
              </div>,
            ]}
            targetKeys={usuariosAsignados.map((u) => u.id)}
            onChange={handleTransferChange}
            render={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <UserOutlined
                      style={{ fontSize: "24px", color: "#1890ff" }}
                    />
                  }
                  title={
                    <Text strong style={{ fontSize: "16px" }}>
                      {item.title}
                    </Text>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: "14px" }}>
                      {item.description}
                    </Text>
                  }
                />
              </List.Item>
            )}
            loading={transferLoading}
            listStyle={{
              width: 500,
              height: 600,
              padding: "24px",
            }}
            operations={["Asignar →", "← Quitar"]}
            showSearch
            filterOption={(inputValue, item) =>
              item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !==
                -1 ||
              item.description
                .toLowerCase()
                .indexOf(inputValue.toLowerCase()) !== -1
            }
          />
        </Card>
      </Modal>

      {/* Modal para Gestionar Zonas */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EnvironmentOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
            <span style={{ fontSize: "18px" }}>
              Gestionar Zonas - {rolSeleccionado?.nombre_rol}
            </span>
          </div>
        }
        open={zonasModalVisible}
        onCancel={() => setZonasModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setZonasModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={guardandoZonas}
            onClick={handleGuardarZonas}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Guardar Zonas
          </Button>,
        ]}
        width={600}
        styles={{
          body: {
            padding: "24px",
          },
        }}
      >
        <Card variant="borderless">
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Seleccione las zonas que podrá visualizar este rol en el módulo
              de Control de Servicios. Si no se asignan zonas, el rol podrá ver
              todos los registros.
            </Text>
          </div>

          {zonasLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Cargando zonas...</Text>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <Checkbox
                  indeterminate={
                    zonasAsignadas.length > 0 &&
                    zonasAsignadas.length < zonasDisponibles.length
                  }
                  checked={
                    zonasDisponibles.length > 0 &&
                    zonasAsignadas.length === zonasDisponibles.length
                  }
                  onChange={(e) => handleSeleccionarTodasZonas(e.target.checked)}
                >
                  <Text strong>
                    Seleccionar todas ({zonasAsignadas.length} de{" "}
                    {zonasDisponibles.length})
                  </Text>
                </Checkbox>
              </div>

              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                {zonasDisponibles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Text type="secondary">No hay zonas disponibles</Text>
                  </div>
                ) : (
                  <List
                    dataSource={zonasDisponibles}
                    renderItem={(zona) => (
                      <List.Item
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          backgroundColor: zonasAsignadas.includes(zona.id)
                            ? "#f6ffed"
                            : "transparent",
                          borderRadius: "4px",
                          marginBottom: "4px",
                        }}
                        onClick={() =>
                          handleZonaChange(zona.id, !zonasAsignadas.includes(zona.id))
                        }
                      >
                        <Checkbox
                          checked={zonasAsignadas.includes(zona.id)}
                          onChange={(e) =>
                            handleZonaChange(zona.id, e.target.checked)
                          }
                        >
                          <Space>
                            <EnvironmentOutlined
                              style={{
                                color: zonasAsignadas.includes(zona.id)
                                  ? "#52c41a"
                                  : "#8c8c8c",
                              }}
                            />
                            <Text
                              strong={zonasAsignadas.includes(zona.id)}
                              style={{
                                color: zonasAsignadas.includes(zona.id)
                                  ? "#52c41a"
                                  : "inherit",
                              }}
                            >
                              {zona.nombre}
                            </Text>
                          </Space>
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                )}
              </div>

              {zonasAsignadas.length === 0 && (
                <div
                  style={{
                    marginTop: 16,
                    padding: "12px",
                    backgroundColor: "#fffbe6",
                    borderRadius: "8px",
                    border: "1px solid #ffe58f",
                  }}
                >
                  <Text type="warning">
                    Sin zonas asignadas, este rol podrá ver todos los
                    registros de Control de Servicios.
                  </Text>
                </div>
              )}
            </>
          )}
        </Card>
      </Modal>

      {/* Modal para Gestionar EPS */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <BankOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
            <span style={{ fontSize: "18px" }}>
              Gestionar EPS - {rolSeleccionado?.nombre_rol}
            </span>
          </div>
        }
        open={epsModalVisible}
        onCancel={() => setEpsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEpsModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={guardandoEps}
            onClick={handleGuardarEps}
          >
            Guardar EPS
          </Button>,
        ]}
        width={600}
        styles={{
          body: {
            padding: "24px",
          },
        }}
      >
        <Card variant="borderless">
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Seleccione las EPS (Clientes) que podrá visualizar este rol en los
              módulos TAI. Si no se asignan EPS, el rol podrá ver todos los
              registros.
            </Text>
          </div>

          {epsLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Cargando EPS...</Text>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <Checkbox
                  indeterminate={
                    epsAsignadas.length > 0 &&
                    epsAsignadas.length < epsDisponibles.length
                  }
                  checked={
                    epsDisponibles.length > 0 &&
                    epsAsignadas.length === epsDisponibles.length
                  }
                  onChange={(e) => handleSeleccionarTodasEps(e.target.checked)}
                >
                  <Text strong>
                    Seleccionar todas ({epsAsignadas.length} de{" "}
                    {epsDisponibles.length})
                  </Text>
                </Checkbox>
              </div>

              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                  padding: "8px",
                }}
              >
                {epsDisponibles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Text type="secondary">No hay EPS disponibles</Text>
                  </div>
                ) : (
                  <List
                    dataSource={epsDisponibles}
                    renderItem={(eps) => (
                      <List.Item
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          backgroundColor: epsAsignadas.includes(eps.id)
                            ? "#e6f7ff"
                            : "transparent",
                          borderRadius: "4px",
                          marginBottom: "4px",
                        }}
                        onClick={() =>
                          handleEpsChange(eps.id, !epsAsignadas.includes(eps.id))
                        }
                      >
                        <Checkbox
                          checked={epsAsignadas.includes(eps.id)}
                          onChange={(e) =>
                            handleEpsChange(eps.id, e.target.checked)
                          }
                        >
                          <Space>
                            <BankOutlined
                              style={{
                                color: epsAsignadas.includes(eps.id)
                                  ? "#1890ff"
                                  : "#8c8c8c",
                              }}
                            />
                            <Text
                              strong={epsAsignadas.includes(eps.id)}
                              style={{
                                color: epsAsignadas.includes(eps.id)
                                  ? "#1890ff"
                                  : "inherit",
                              }}
                            >
                              {eps.nombre}
                            </Text>
                          </Space>
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                )}
              </div>

              {epsAsignadas.length === 0 && (
                <div
                  style={{
                    marginTop: 16,
                    padding: "12px",
                    backgroundColor: "#fffbe6",
                    borderRadius: "8px",
                    border: "1px solid #ffe58f",
                  }}
                >
                  <Text type="warning">
                    Sin EPS asignadas, este rol podrá ver todos los registros
                    de autorizaciones y traslados TAI.
                  </Text>
                </div>
              )}
            </>
          )}
        </Card>
      </Modal>
    </div>
  );
};

export default RolesList;
